import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, ChevronRight, ChevronDown, BadgeCheck, Star, Users, ArrowRight, Palette, Wrench, Briefcase, Feather, Code, UtensilsCrossed, Heart, Car, HardHat, GraduationCap, Video, UserCheck, Shirt, Music, Calculator, Wheat, Scissors, Sparkles, Baby, Sparkle, HeartHandshake, PartyPopper, Leaf, Dumbbell, Stethoscope, Hammer, Hotel, Shield, Scale, Megaphone, Truck, PawPrint, Home, ShieldCheck, HeartPulse, Bus, PenTool, TreeDeciduous, Megaphone as MegaphoneIcon, DollarSign, HandHeart, Network, Crown, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HomeCommunitySections from '@/components/home/HomeCommunitySections';
import HomeFeedPreview from '@/components/home/HomeFeedPreview';
import HomepageContactForm from '@/components/HomepageContactForm';
import useSeo from '@/hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// New hero video — clean abstract purple/blue (no embedded text)
const HERO_VIDEO_URL = 'https://customer-assets.emergentagent.com/job_d3c91071-df90-475c-8737-7686ffc2cb82/artifacts/2f4rzpdn_Purple%20and%20Blue%20Dynamic%20Crypto%20Investment%20Mobile%20Video%20%281%29.mp4';

const categoryIcons = {
  'Creative': Palette, 'Trades': Wrench, 'Professional': Briefcase, 'Cultural': Feather,
  'Technology': Code, 'Food & Beverage': UtensilsCrossed, 'Wellness': Heart, 'Automotive': Car,
  'Construction': HardHat, 'Education': GraduationCap, 'Media': Video, 'Consulting': UserCheck,
  'Fashion': Shirt, 'Music': Music, 'Accounting & Finance': Calculator, 'Agriculture & Farming': Wheat,
  'Arts & Crafts': Scissors, 'Beauty & Personal Care': Sparkles, 'Childcare & Education': Baby,
  'Cleaning Services': Sparkle, 'Counseling & Therapy': HeartHandshake, 'Entertainment & Events': PartyPopper,
  'Environmental Services': Leaf, 'Fitness & Sports': Dumbbell, 'Healthcare & Medical': Stethoscope,
  'Home Improvement': Hammer, 'Hospitality & Tourism': Hotel, 'Insurance Services': Shield,
  'Landscaping & Gardening': TreeDeciduous, 'Legal Services': Scale, 'Marketing & Advertising': MegaphoneIcon,
  'Moving & Storage': Truck, 'Pet Services': PawPrint, 'Real Estate': Home,
  'Security Services': ShieldCheck, 'Senior Care': HeartPulse, 'Transportation': Bus, 'Writing & Translation': PenTool,
};

// Brand-matched dark glow card (deep navy w/ purple/magenta glow border)
const DARK_GLOW_CARD = "relative rounded-2xl bg-[#13153A]/80 backdrop-blur-md border border-purple-500/30 shadow-[0_10px_40px_-10px_rgba(168,85,247,0.4)] hover:shadow-[0_18px_60px_-10px_rgba(236,72,153,0.55)] hover:border-pink-400/60 transition-all duration-300";

// 6-step flowchart matching the brand journey
const FLOW_STEPS = [
  {
    n: '01',
    icon: Crown,
    title: 'Build Your Brand',
    desc: 'Create a profile that showcases your work, your story, and your services.',
    color: 'from-fuchsia-500 to-purple-600',
    glow: 'rgba(217,70,239,0.6)',
  },
  {
    n: '02',
    icon: Megaphone,
    title: 'Promote Your Business',
    desc: 'Get discovered. Be featured. Turn visibility into income.',
    color: 'from-pink-500 to-rose-500',
    glow: 'rgba(236,72,153,0.6)',
  },
  {
    n: '03',
    icon: DollarSign,
    title: 'Get Booked & Paid',
    desc: 'Offer services. Set your prices. Secure real opportunities.',
    color: 'from-violet-500 to-indigo-500',
    glow: 'rgba(139,92,246,0.6)',
  },
  {
    n: '04',
    icon: HandHeart,
    title: 'Support Native Businesses',
    desc: 'Spend within the community. Circulate wealth across Turtle Island.',
    color: 'from-cyan-400 to-blue-500',
    glow: 'rgba(34,211,238,0.6)',
  },
  {
    n: '05',
    icon: Network,
    title: 'Connect with Creatives',
    desc: 'Collaborate with artists, designers, entrepreneurs & visionaries.',
    color: 'from-purple-500 to-fuchsia-500',
    glow: 'rgba(168,85,247,0.6)',
  },
  {
    n: '06',
    icon: Rocket,
    title: 'Build Legacy & Community',
    desc: 'Be part of something bigger. Uplift culture. Be part of history.',
    color: 'from-amber-400 to-pink-500',
    glow: 'rgba(245,158,11,0.6)',
  },
];

export default function HomePage() {
  useSeo({
    title: 'EntrepreNatives — Indigenous Marketplace',
    description: 'Discover, verify, and support Indigenous-owned businesses worldwide. Shop handmade goods, book services, and connect with Native American, First Nations, Alaska Native, Hawaiian, Métis & Inuit vendors — all protected by secure escrow payments.',
    url: 'https://entreprenatives.com/',
  });

  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [platformStats, setPlatformStats] = useState({ total_categories: 46, total_subcategories: 332 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [location, setLocation] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const dropdownRef = useRef(null);
  const [cmsBlocks, setCmsBlocks] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchFeaturedVendors();
    fetchPlatformStats();
    seedCategories();
    fetchCMSContent();
  }, []);

  const fetchCMSContent = async () => {
    try {
      const response = await fetch(`${API}/marketplace/pages/home`);
      if (response.ok) {
        const data = await response.json();
        if (data.blocks?.length > 0) {
          setCmsBlocks(data.blocks.filter(b => b.is_visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
      }
    } catch (error) { /* fallback */ }
  };

  const getCmsBlock = (type) => cmsBlocks?.find(b => b.type === type)?.content || null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExpandedCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const seedCategories = async () => {
    try { await fetch(`${API}/seed-categories`, { method: 'POST' }); } catch {}
  };

  const fetchPlatformStats = async () => {
    try {
      const response = await fetch(`${API}/stats/platform`);
      if (response.ok) setPlatformStats(await response.json());
    } catch {}
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const data = await response.json();
        const sorted = [...data]
          .map((c) => ({
            ...c,
            subcategories: [...(c.subcategories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(sorted);
      }
    } catch {}
  };

  const fetchFeaturedVendors = async () => {
    try {
      const response = await fetch(`${API}/vendors/featured?limit=8`);
      if (response.ok) {
        const data = await response.json();
        // Homepage feature: only show vendors with profile pictures (curated showcase)
        setFeaturedVendors((data || []).filter(v => v?.profile_image || v?.user_picture));
      }
    } catch {}
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (location) params.set('location', location);
    navigate(`/browse?${params.toString()}`);
  };

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  return (
    <div className="min-h-screen bg-[#0B0D2B] relative">
      <Navbar />

      {/* ─────────────────── HERO with Video Background ─────────────────── */}
      <section className="relative min-h-[100vh] flex items-center justify-center pt-20 overflow-hidden bg-[#0B0D2B]">
        <video
          autoPlay muted loop playsInline preload="auto"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover z-0"
          data-testid="hero-video-bg"
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>

        {/* Brand-matched overlays — keep deep-navy mood, push magenta/purple glow toward center */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D2B]/30 via-[#0B0D2B]/55 to-[#0B0D2B]/85 z-[1]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_55%,rgba(192,38,211,0.30),transparent_60%)] z-[1]"></div>

        <div className="container-main relative z-10 py-20">
          <motion.div className="max-w-5xl mx-auto text-center" variants={containerVariants} initial="hidden" animate="visible">
            <motion.h1
              className="font-syne text-[2.25rem] xs:text-5xl sm:text-6xl lg:text-8xl font-bold text-white leading-[0.95] sm:leading-none mb-8 px-2 break-words tracking-tight"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.85), 0 0 80px rgba(217,70,239,0.45)' }}
              variants={itemVariants}
              data-testid="hero-title"
            >
              {(getCmsBlock('marketplace_hero')?.title_line1) || 'SUPPORT'}<br />
              <span className="text-holographic">{(getCmsBlock('marketplace_hero')?.title_line2) || 'INDIGENOUS'}</span><br />
              {(getCmsBlock('marketplace_hero')?.title_line3) || 'BUSINESSES'}
            </motion.h1>

            <motion.p
              className="font-outfit text-base sm:text-lg md:text-xl text-white/95 max-w-2xl mx-auto mb-12 font-medium px-2"
              style={{ textShadow: '0 2px 12px rgba(0,0,0,0.85)' }}
              variants={itemVariants}
              data-testid="hero-subtitle"
            >
              {(getCmsBlock('marketplace_hero')?.subtitle) || 'Connect with verified Indigenous vendors across every industry. Secure escrow protection. Support the community.'}
            </motion.p>

            <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10" variants={itemVariants}>
              <Link to="/browse" data-testid="cta-hire-vendor">
                <Button className="btn-primary bg-yellow-400 hover:bg-yellow-300 text-black text-base">
                  Hire a Vendor<ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/auth?mode=register&intent=vendor" data-testid="cta-become-vendor">
                <Button variant="outline" className="btn-secondary text-base">Become a Vendor</Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10" animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <div className="w-6 h-10 border-2 border-white/70 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white rounded-full"></div>
          </div>
        </motion.div>
      </section>

      {/* ─────────────────── THE FEED — moved below "Recently Added Vendors" ─────────────────── */}

      {/* ─────────────────── HOW IT WORKS — Custom Zigzag Flowchart ─────────────────── */}
      <section
        className="relative py-24 overflow-hidden bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B]"
        data-testid="how-it-works-flowchart-section"
      >
        {/* Glowing brand-matched blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-fuchsia-600/20 blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-purple-700/15 blur-3xl"></div>
        </div>

        <div className="container-main relative">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2
              className="font-syne text-[1.6rem] xs:text-3xl sm:text-4xl md:text-6xl font-black text-white mb-4 break-words px-2 tracking-tight"
              style={{ textShadow: '0 0 40px rgba(217,70,239,0.5)' }}
            >
              HOW IT <span className="text-holographic">WORKS</span>
            </h2>
            <p className="text-zinc-300 text-lg md:text-xl font-outfit max-w-2xl mx-auto">
              Your journey on EntrepreNatives — six steps from spark to legacy.
            </p>
          </motion.div>

          {/* Vertical timeline with alternating zig-zag cards */}
          <div className="relative max-w-5xl mx-auto">
            {/* Center gradient spine (desktop) / left spine (mobile) */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-1 -translate-x-1/2 bg-gradient-to-b from-fuchsia-500 via-purple-500 via-pink-500 to-cyan-400 opacity-50 rounded-full"></div>

            <div className="space-y-10 md:space-y-14">
              {FLOW_STEPS.map((step, i) => {
                const isLeft = i % 2 === 0;
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.n}
                    className="relative"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: i * 0.05 }}
                    data-testid={`flow-step-${i + 1}`}
                  >
                    {/* Numbered glowing node on the spine */}
                    <div
                      className={`absolute left-6 md:left-1/2 top-4 md:top-6 -translate-x-1/2 w-14 h-14 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-syne font-black text-lg z-20 border-4 border-[#0B0D2B]`}
                      style={{ boxShadow: `0 0 30px ${step.glow}, 0 0 60px ${step.glow}` }}
                    >
                      {step.n}
                    </div>

                    {/* Card — full width on mobile (left-padded past the spine), half-width on desktop alternating */}
                    <div className={`pl-20 md:pl-0 md:w-[calc(50%-3rem)] ${isLeft ? 'md:pr-0' : 'md:ml-auto'}`}>
                      <div className={`${DARK_GLOW_CARD} p-6 md:p-7`}>
                        <div className="flex items-start gap-4 mb-3">
                          <div
                            className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0`}
                            style={{ boxShadow: `0 8px 24px -6px ${step.glow}` }}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <h3
                            className="font-syne text-lg sm:text-xl md:text-2xl font-black text-holographic leading-tight pt-1 tracking-tight break-words"
                          >
                            {step.title}
                          </h3>
                        </div>
                        <p className="font-outfit text-zinc-300 text-base md:text-lg leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/auth?mode=register&role=vendor">
              <Button className="btn-primary bg-yellow-400 hover:bg-yellow-300 text-black text-base">
                Start Your Journey<ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ─────────────────── Categories Section ─────────────────── */}
      <section
        className="py-20 lg:py-24 relative bg-gradient-to-b from-[#0B0D2B] via-[#1E1B4B] to-[#0B0D2B] overflow-hidden"
        data-testid="categories-section"
      >
        <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-fuchsia-600/20 blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-20 left-0 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none"></div>
        <div className="container-main relative">
          <motion.div className="mb-12 text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2
              className="font-syne text-[1.6rem] xs:text-3xl sm:text-4xl md:text-6xl font-black text-white mb-3 break-words px-2 tracking-tight"
              style={{ textShadow: '0 0 40px rgba(217,70,239,0.45)' }}
            >
              {(getCmsBlock('category_grid')?.title) || 'EXPLORE'}{' '}
              <span className="text-holographic">
                {(getCmsBlock('category_grid')?.title_highlight) || 'CATEGORIES'}
              </span>
            </h2>
            <p className="text-zinc-300 font-outfit text-base lg:text-lg">
              {(getCmsBlock('category_grid')?.subtitle) || 'Indigenous businesses across every industry'}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 lg:gap-4">
            {categories.slice(0, 8).map((category, index) => {
              const IconComponent = categoryIcons[category.name] || Briefcase;
              return (
                <motion.div key={category.category_id} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.04 }}>
                  <Link to={`/browse?category=${category.category_id}`}
                    className={`${DARK_GLOW_CARD} flex items-center gap-3 p-4 hover:scale-[1.02] group`}
                    data-testid={`category-card-${category.slug}`}>
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-[0_8px_20px_-5px_rgba(217,70,239,0.6)] group-hover:shadow-[0_10px_28px_-5px_rgba(236,72,153,0.85)] transition-all">
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-outfit font-bold text-white text-[13px] sm:text-sm leading-tight line-clamp-2">{category.name}</h3>
                      <p className="text-zinc-400 text-xs font-outfit">{category.subcategories?.length || 0} types</p>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {categories.length > 8 && (
            <>
              <AnimatePresence>
                {showAllCategories && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 lg:gap-4 mt-3 lg:mt-4">
                      {categories.slice(8).map((category, index) => {
                        const IconComponent = categoryIcons[category.name] || Briefcase;
                        return (
                          <motion.div key={category.category_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.02 }}>
                            <Link to={`/browse?category=${category.category_id}`}
                              className={`${DARK_GLOW_CARD} flex items-center gap-3 p-4 hover:scale-[1.02] group`}
                              data-testid={`category-card-${category.slug}`}>
                              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-[0_8px_20px_-5px_rgba(217,70,239,0.6)] group-hover:shadow-[0_10px_28px_-5px_rgba(236,72,153,0.85)] transition-all">
                                <IconComponent className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="font-outfit font-bold text-white text-[13px] sm:text-sm leading-tight line-clamp-2">{category.name}</h3>
                                <p className="text-zinc-400 text-xs font-outfit">{category.subcategories?.length || 0} types</p>
                              </div>
                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="text-center mt-8">
                <button onClick={() => setShowAllCategories(!showAllCategories)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white bg-[#13153A] border-2 border-fuchsia-500/40 hover:border-fuchsia-400 hover:bg-fuchsia-500/10 hover:shadow-[0_8px_30px_-8px_rgba(217,70,239,0.6)] transition-all"
                  data-testid="toggle-all-categories">
                  {showAllCategories ? 'Show Less' : `View All ${categories.length} Categories`}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ─────────────────── Featured Vendors Section ─────────────────── */}
      {featuredVendors.length > 0 && (
        <section className="py-24 relative bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B] overflow-hidden" data-testid="featured-vendors-section">
          <div className="absolute -bottom-32 left-0 w-[500px] h-[500px] rounded-full bg-pink-500/15 blur-3xl pointer-events-none"></div>
          <div className="container-main relative">
            <motion.div className="flex items-end justify-between mb-14" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div>
                <h2 className="font-syne text-[1.6rem] xs:text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 break-words px-2 tracking-tight" style={{ textShadow: '0 0 40px rgba(217,70,239,0.45)' }}>
                  {(getCmsBlock('featured_vendors')?.title) || 'FEATURED'}{' '}
                  <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent">
                    {(getCmsBlock('featured_vendors')?.title_highlight) || 'ELITE'}
                  </span>{' '}
                  {(getCmsBlock('featured_vendors')?.title_suffix) || 'VENDORS'}
                </h2>
                <p className="text-zinc-300 font-outfit text-lg">
                  {(getCmsBlock('featured_vendors')?.subtitle) || 'Top-rated Indigenous businesses'}
                </p>
              </div>
              <Link to="/browse" className="hidden md:flex items-center text-fuchsia-300 hover:text-fuchsia-100 font-outfit font-bold transition-colors">
                View All <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredVendors.map((vendor, index) => (
                <motion.div key={vendor.vendor_id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                  <Link to={`/vendor/${vendor.vendor_id}`} className={`${DARK_GLOW_CARD} block p-6 group`} data-testid={`vendor-card-${vendor.vendor_id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#1E1B4B] flex-shrink-0 ring-2 ring-fuchsia-500/30">
                        {vendor.profile_image || vendor.user_picture ? (
                          <img src={vendor.profile_image || vendor.user_picture} alt={vendor.business_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-500"><Users className="w-8 h-8" /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-outfit font-bold text-white truncate group-hover:text-fuchsia-300 transition-colors">{vendor.business_name}</h3>
                        <p className="text-zinc-400 text-sm truncate">{vendor.location}</p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {vendor.membership_level === 'elite' && <span className="badge-elite"><BadgeCheck className="w-3 h-3" /> Elite</span>}
                          {vendor.vendor_type === 'verified' && <span className="badge-verified"><BadgeCheck className="w-3 h-3" /> Verified</span>}
                          {vendor.vendor_type === 'startup' && <span className="badge-startup">Startup</span>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-fuchsia-500/20 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-white font-mono text-sm font-bold">{vendor.rating?.toFixed(1) || 'New'}</span>
                        <span className="text-zinc-400 text-sm">({vendor.review_count || 0})</span>
                      </div>
                      {vendor.starting_price && <span className="text-zinc-300 font-mono text-sm">From ${vendor.starting_price}</span>}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────── First Indigenous Marketplace Banner ─────────────────── */}
      <section className="py-24 relative bg-gradient-to-b from-[#0B0D2B] via-[#1E1B4B] to-[#0B0D2B] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-fuchsia-600/25 via-pink-500/15 to-cyan-500/20 blur-3xl"></div>
        </div>
        <div className="container-main relative">
          <motion.div className="text-center max-w-4xl mx-auto" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2
              className="font-syne text-[1.6rem] xs:text-3xl sm:text-4xl md:text-6xl font-black text-white mb-6 leading-[1.08] sm:leading-[1.05] break-words px-2 tracking-tight"
              style={{ textShadow: '0 0 50px rgba(217,70,239,0.55)' }}
            >
              THE FIRST{' '}
              <span className="text-holographic">
                INDIGENOUS
              </span>
              <br />BUSINESS MARKETPLACE
            </h2>
            <p className="text-zinc-200 font-outfit text-lg md:text-xl max-w-3xl mx-auto mb-5 leading-relaxed">
              Find and support Indigenous businesses in every industry. Hire vendors, book services, and uplift Native communities — all in one place.
            </p>
            <p
              className="font-outfit font-bold tracking-wide text-base md:text-lg bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent"
              data-testid="hero-tagline"
            >
              Shop Native, Support Native Communities
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─────────────────── Community Impact Section ─────────────────── */}
      <section
        className="py-24 relative bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B] overflow-hidden"
        data-testid="community-impact-section"
      >
        <div className="absolute -top-20 right-1/4 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/3 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none"></div>
        <div className="container-main relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2
                className="font-syne text-[1.4rem] xs:text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-black text-white mb-8 leading-tight px-1 tracking-tight"
                style={{ textShadow: '0 0 40px rgba(217,70,239,0.45)' }}
              >
                {(getCmsBlock('community_impact')?.title_line1) || 'WHY'}<br />
                <span className="text-holographic">
                  {(getCmsBlock('community_impact')?.title_highlight) || 'ENTREPRENATIVES'}
                </span><br />
                {(getCmsBlock('community_impact')?.title_line3) || 'EXISTS'}
              </h2>
              <div className="space-y-6 text-zinc-200 font-outfit text-lg leading-relaxed">
                {(getCmsBlock('community_impact')?.paragraphs || [
                  'Indigenous businesses represent less than 1% of businesses nationwide, yet exist in nearly every industry.',
                  'The lack of visibility and access to capital has historically limited Indigenous business growth. EntrepreNatives changes that.',
                  'When you support Indigenous businesses, you\'re investing in communities and building a more equitable economy.'
                ]).map((p, i) => (
                  <p key={i} className={i === ((getCmsBlock('community_impact')?.paragraphs || []).length - 1) ? 'text-white font-bold' : ''}>{p}</p>
                ))}
              </div>
              <Link to="/auth?mode=register" className="inline-block mt-10" data-testid="impact-cta">
                <Button className="btn-primary bg-yellow-400 hover:bg-yellow-300 text-black">
                  Start Supporting Today<ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div className="relative" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className={`${DARK_GLOW_CARD} text-center p-4 sm:p-8`}>
                  <span className="block font-syne text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2 tracking-tight" style={{ textShadow: '0 0 30px rgba(217,70,239,0.6)' }}>100%</span>
                  <span className="text-zinc-400 font-outfit text-xs sm:text-sm font-semibold">Verified Vendors</span>
                </div>
                <div className={`${DARK_GLOW_CARD} text-center p-4 sm:p-8`}>
                  <span className="block font-syne text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent mb-2 tracking-tight">SAFE</span>
                  <span className="text-zinc-400 font-outfit text-xs sm:text-sm font-semibold">Escrow Protection</span>
                </div>
                <div className={`${DARK_GLOW_CARD} text-center p-4 sm:p-8`}>
                  <span className="block font-syne text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2 tracking-tight" style={{ textShadow: '0 0 30px rgba(34,211,238,0.6)' }}>{platformStats.total_categories}+</span>
                  <span className="text-zinc-400 font-outfit text-xs sm:text-sm font-semibold">Industry Categories</span>
                </div>
                <div className={`${DARK_GLOW_CARD} text-center p-4 sm:p-8`}>
                  <span className="block font-syne text-3xl sm:text-4xl md:text-5xl font-black text-amber-400 mb-2 tracking-tight" style={{ textShadow: '0 0 30px rgba(245,158,11,0.7)' }}>{platformStats.total_subcategories}+</span>
                  <span className="text-zinc-400 font-outfit text-xs sm:text-sm font-semibold">Subcategories</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <HomeCommunitySections />

      {/* ─────────────────── THE FEED — Live community posts (moved below Recently Added) ─────────────────── */}
      <HomeFeedPreview />

      <HomepageContactForm />

      {/* ─────────────────── Featured Founding Vendor Spotlight: Finest Feathers ─────────────────── */}
      <section
        className="relative py-24 overflow-hidden bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B]"
        data-testid="finest-feathers-spotlight"
      >
        {/* Glow blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-fuchsia-600/20 blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl"></div>
        </div>

        <div className="container-main relative">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 text-amber-400 text-[11px] font-bold uppercase tracking-[0.32em] mb-4 px-4 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30">
              ★ Founding Vendor #1 · Spotlight
            </span>
            <h2
              className="font-syne text-[1.6rem] xs:text-3xl sm:text-4xl md:text-6xl font-black text-white mb-4 break-words px-2 tracking-tight"
            >
              PURCHASE <span className="text-holographic">MERCHANDISE</span>
            </h2>
            <p className="text-zinc-300 text-base sm:text-lg md:text-xl font-outfit max-w-2xl mx-auto px-2">
              Authentic Indigenous-made goods from <span className="text-amber-300 font-semibold">Finest Feathers</span> — the founding vendor that started it all.
            </p>
          </motion.div>

          {/* Square live preview of finestfeathers.com */}
          <motion.a
            href="https://www.finestfeathers.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block max-w-[480px] mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="finest-feathers-link"
          >
            {/* Outer glowing rainbow ring */}
            <div className="absolute -inset-1.5 rounded-3xl bg-gradient-to-r from-fuchsia-500 via-amber-400 to-cyan-400 opacity-60 blur-md group-hover:opacity-90 group-hover:blur-lg transition-all duration-500"></div>

            <div className="relative aspect-square w-full rounded-3xl overflow-hidden border-4 border-white/15 bg-black shadow-[0_30px_80px_-15px_rgba(217,70,239,0.55)] transform transition-transform duration-500 group-hover:scale-[1.02]">
              {/* Live iframe acting as a video poster */}
              <iframe
                src="https://www.finestfeathers.com"
                title="Finest Feathers — Indigenous Merchandise"
                loading="lazy"
                className="absolute top-0 left-0 w-[180%] h-[180%] -translate-x-[22%] -translate-y-[22%] pointer-events-none border-0"
                tabIndex={-1}
                aria-hidden="true"
              />

              {/* Soft top + bottom gradient for legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30 pointer-events-none"></div>

              {/* Floating sparkle icon top-left */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/95 text-black text-[10px] font-black uppercase tracking-wider z-10 shadow-lg">
                ★ Live Site
              </div>

              {/* Bottom CTA */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7 z-10">
                <div className="flex items-end justify-between gap-3">
                  <div className="text-left">
                    <p className="font-syne font-black text-white text-xl sm:text-2xl leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]">
                      Finest Feathers
                    </p>
                    <p className="text-amber-200 text-xs sm:text-sm font-outfit drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
                      Visit shop · finestfeathers.com
                    </p>
                  </div>
                  <span className="inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-yellow-400 text-black shadow-[0_0_24px_rgba(250,204,21,0.6)] group-hover:bg-yellow-300 group-hover:scale-110 transition-all flex-shrink-0">
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </div>
              </div>
            </div>
          </motion.a>

          <p className="text-center text-zinc-500 text-xs font-outfit mt-6 max-w-md mx-auto">
            Opens finestfeathers.com in a new tab.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
